import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useIntl } from "../lib/i18n";
import { api } from "../lib/api";

// DraftChatPanel implements the multi-turn chat seam (epic 32).
// Lives on DraftDetailPage; consumes /tenant/drafts/{id}/turns +
// /tenant/drafts/{id}/chat (SSE) + /tenant/drafts/{id}/turns/{turnId}/apply.
//
// SSE is streamed via fetch + a manual ReadableStream parser — the SPA
// does not depend on EventSource (EventSource has no Authorization-header
// support).

type Turn = {
  id: string;
  turnIndex: number;
  role: "USER" | "ASSISTANT";
  content: string;
  bodySnapshot?: string;
  createdAt: string;
};

export function DraftChatPanel({ draftId, onApplied }: { draftId: string; onApplied?: () => void }) {
  const { session } = useAuth();
  const { t } = useIntl();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [message, setMessage] = useState("");
  const [streamingBody, setStreamingBody] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diffFrom, setDiffFrom] = useState<string | null>(null);
  const [diffTo, setDiffTo] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api(`/api/v1/tenant/drafts/${encodeURIComponent(draftId)}/turns`, {
        token: session?.token,
      });
      if (!res.ok) throw new Error(await res.text());
      const body = (await res.json()) as { turns: Turn[] };
      setTurns(body.turns ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [draftId, session?.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async () => {
    if (!message.trim()) return;
    setError(null);
    setStreaming(true);
    setStreamingBody("");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`/api/v1/tenant/drafts/${encodeURIComponent(draftId)}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${session?.token ?? ""}`,
        },
        body: JSON.stringify({ message }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`stream failed: ${res.status} ${await res.text().catch(() => "")}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() ?? "";
        for (const evt of events) {
          const lines = evt.split("\n").filter(Boolean);
          let kind = "message";
          let data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) kind = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (!data) continue;
          try {
            const json = JSON.parse(data);
            if (kind === "chunk" && json.delta) {
              setStreamingBody((s) => s + json.delta);
            } else if (kind === "error" && json.message) {
              setError(json.message);
            }
          } catch {
            // ignore non-json
          }
        }
      }
      setMessage("");
      setStreamingBody("");
      await load();
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const apply = async (turnId: string) => {
    setError(null);
    try {
      const res = await api(
        `/api/v1/tenant/drafts/${encodeURIComponent(draftId)}/turns/${encodeURIComponent(turnId)}/apply`,
        { method: "POST", token: session?.token, body: JSON.stringify({}) },
      );
      if (!res.ok) throw new Error(await res.text());
      onApplied?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // 32.5 diff: pick two assistant turns; show side-by-side with green/red highlights.
  const assistantTurns = turns.filter((tu) => tu.role === "ASSISTANT");
  const fromTurn = assistantTurns.find((tu) => tu.id === diffFrom);
  const toTurn = assistantTurns.find((tu) => tu.id === diffTo);
  const diff = fromTurn && toTurn ? lineDiff(fromTurn.bodySnapshot ?? "", toTurn.bodySnapshot ?? "") : null;

  return (
    <section className="mt-6 rounded-md border border-slate-200 bg-white p-3">
      <h2 className="text-sm font-semibold text-brand-900">{t("drafts.chat.title")}</h2>
      <p className="mt-1 text-xs text-slate-500">{t("drafts.chat.help")}</p>

      <div className="mt-3 max-h-[40vh] space-y-2 overflow-auto rounded bg-slate-50 p-2 text-sm">
        {turns.length === 0 && <p className="text-slate-500">{t("drafts.chat.empty")}</p>}
        {turns.map((tu) => (
          <div
            key={tu.id}
            className={
              tu.role === "USER"
                ? "rounded bg-white px-3 py-2 ring-1 ring-slate-200"
                : "rounded bg-brand-50 px-3 py-2 ring-1 ring-brand-200"
            }
          >
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                <strong>{tu.role}</strong> · #{tu.turnIndex} ·{" "}
                {new Date(tu.createdAt).toLocaleTimeString()}
              </span>
              {tu.role === "ASSISTANT" && (
                <span className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => apply(tu.id)}
                    className="text-xs font-medium text-brand-700 hover:underline"
                  >
                    {t("drafts.chat.apply")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiffFrom(tu.id)}
                    className={diffFrom === tu.id ? "text-xs font-bold text-emerald-700" : "text-xs text-slate-500 hover:underline"}
                  >
                    A
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiffTo(tu.id)}
                    className={diffTo === tu.id ? "text-xs font-bold text-rose-700" : "text-xs text-slate-500 hover:underline"}
                  >
                    B
                  </button>
                </span>
              )}
            </div>
            <pre className="mt-1 whitespace-pre-wrap text-xs">{tu.content}</pre>
          </div>
        ))}
        {streaming && streamingBody && (
          <div className="rounded bg-brand-50 px-3 py-2 ring-1 ring-brand-200">
            <div className="text-xs font-medium text-slate-500">{t("drafts.chat.streaming")}</div>
            <pre className="mt-1 whitespace-pre-wrap text-xs">{streamingBody}</pre>
          </div>
        )}
      </div>

      {diff && (
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-2 text-xs">
          <div className="mb-1 font-semibold text-slate-700">
            {t("drafts.chat.diff")} (A → B)
          </div>
          <pre className="overflow-auto whitespace-pre-wrap">
            {diff.map((d, i) => (
              <span
                key={i}
                className={
                  d.kind === "add"
                    ? "block bg-emerald-100"
                    : d.kind === "del"
                      ? "block bg-rose-100 line-through"
                      : "block"
                }
              >
                {d.kind === "add" ? "+ " : d.kind === "del" ? "- " : "  "}
                {d.line}
              </span>
            ))}
          </pre>
        </div>
      )}

      <div className="mt-3 flex items-start gap-2">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("drafts.chat.placeholder")}
          rows={2}
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
          disabled={streaming}
        />
        <button
          type="button"
          onClick={send}
          disabled={streaming || !message.trim()}
          className="rounded-md bg-brand-700 px-3 py-2 text-sm text-white hover:bg-brand-500 disabled:opacity-50"
        >
          {streaming ? t("drafts.chat.sending") : t("drafts.chat.send")}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
    </section>
  );
}

type DiffLine = { kind: "ctx" | "add" | "del"; line: string };

// lineDiff is a minimal Myers-style line diff. Sufficient for the 32.5
// AC ("additions in green and removals in red"). Cubic on length —
// fine for legal-draft snapshots (a few hundred lines at most).
export function lineDiff(a: string, b: string): DiffLine[] {
  const A = a.split(/\r?\n/);
  const B = b.split(/\r?\n/);
  const m = A.length;
  const n = B.length;
  // LCS table.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = A[i - 1] === B[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (A[i - 1] === B[j - 1]) {
      out.unshift({ kind: "ctx", line: A[i - 1] });
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      out.unshift({ kind: "del", line: A[i - 1] });
      i--;
    } else {
      out.unshift({ kind: "add", line: B[j - 1] });
      j--;
    }
  }
  while (i > 0) {
    out.unshift({ kind: "del", line: A[i - 1] });
    i--;
  }
  while (j > 0) {
    out.unshift({ kind: "add", line: B[j - 1] });
    j--;
  }
  return out;
}
